// Form mínimo para crear una factura draft con line items.
// Para casos avanzados (clonar quote) usar el endpoint POST /from-quote/{id}/.

import { useState } from 'react';

import { useCreateInvoice } from '../hooks/useInvoicing';
import type { LineItemIn } from '../types';

const EMPTY_LINE: LineItemIn = {
  description: '',
  quantity: 1,
  unit_price: 0,
  tax_rate: 0.16,
  retention_rate: 0,
};

interface Props {
  onCreated?: (invoiceId: string) => void;
}

export function InvoiceForm({ onCreated }: Props) {
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerTaxId, setCustomerTaxId] = useState('');
  const [currency, setCurrency] = useState('MXN');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineItemIn[]>([{ ...EMPTY_LINE }]);

  const { submit, loading, error } = useCreateInvoice();

  const updateLine = (idx: number, patch: Partial<LineItemIn>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const onSubmit = async () => {
    const result = await submit({
      line_items: lines,
      customer_name: customerName,
      customer_email: customerEmail,
      customer_tax_id: customerTaxId,
      currency,
      notes,
    });
    if (result && onCreated) onCreated(result.id);
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Cliente</label>
          <input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Email</label>
          <input
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">RFC / Tax ID</label>
          <input
            value={customerTaxId}
            onChange={(e) => setCustomerTaxId(e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Moneda</label>
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Conceptos</h3>
          <button
            onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
            className="text-xs underline"
          >
            + Agregar línea
          </button>
        </div>
        {lines.map((line, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-5">
              <label className="block text-xs text-muted-foreground">Descripción</label>
              <input
                value={line.description}
                onChange={(e) => updateLine(idx, { description: e.target.value })}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-muted-foreground">Cant.</label>
              <input
                type="number" step="0.001"
                value={line.quantity}
                onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-muted-foreground">P. unit.</label>
              <input
                type="number" step="0.01"
                value={line.unit_price}
                onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-muted-foreground">IVA</label>
              <input
                type="number" step="0.0001"
                value={line.tax_rate}
                onChange={(e) => updateLine(idx, { tax_rate: e.target.value })}
                className="w-full border rounded px-2 py-1 text-sm"
              />
            </div>
            <div className="col-span-1">
              <button
                onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                className="text-xs text-destructive"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <label className="block text-xs text-muted-foreground mb-1">Notas</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full border rounded px-2 py-1 text-sm"
          rows={2}
        />
      </div>

      {error && <div className="text-sm text-destructive">Error: {error}</div>}

      <button
        onClick={onSubmit}
        disabled={loading}
        className="bg-primary text-primary-foreground px-4 py-2 rounded text-sm disabled:opacity-50"
      >
        {loading ? 'Creando…' : 'Crear factura draft'}
      </button>
    </div>
  );
}
